<template>
  <div
    class="w-full fixed top-0 bottom-0 left-0 right-0 bg-white z-100 flex flex-col touch-none px-3 py-1.5"
    :style="styles"
  >
    <div class="flex items-center">
      <div
        class="w-full h-[38px] px-3 py-2 flex items-center bg-gray-800/5 rounded-sm relative"
      >
        <div class="flex flex-row-reverse items-center">
          <input
            v-model="query"
            type="text"
            class="peer w-full h-full bg-transparent outline-hidden font-primary text-[14px] leading-[18px] text-gray-800 placeholder:text-gray-800/40 pr-4"
            placeholder="Search"
            ref="input"
          />
          <Icon
            :size="14"
            name="Search"
            class="stroke-gray-800 mr-2.5 peer-placeholder-shown:opacity-40 peer-focus:opacity-100"
          />
        </div>
        <Icon
          v-if="query.length"
          :size="14"
          name="Plus"
          class="absolute right-3 opacity-50 rotate-45 stroke-gray-800"
          @click="handleClearClick"
        />
      </div>
      <div
        class="font-secondary text-[15px] text-gray-800 ml-2.5 -mt-[1px]"
        @click="mobileSearchStore.close"
      >
        Cancel
      </div>
    </div>
    <div id="container" class="overflow-y-auto overscroll-contain">
      <SearchTable
        v-if="showResults"
        :data="results"
        :query="query"
        @clicked="mobileSearchStore.close"
      />
      <div
        v-if="query.length === 1"
        class="w-full h-[60px] flex items-center justify-center font-secondary text-[15px] leading-[18px] text-gray-800 opacity-50"
      >
        Your search must be at least 2 characters long
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useDevice } from '@/shared/composables/useDevice'
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { Icon } from '@dacr/ui'
import { useMobileSearchStore } from '@/features/search/store'
import {
  useSearchLazyQuery,
  type SearchResult,
} from '@/shared/graphql/legacy/generated'
import SearchTable from '@/features/search/components/SearchTable.vue'

const device = useDevice()
const mobileSearchStore = useMobileSearchStore()

const input = ref<HTMLInputElement | undefined>()
const query = ref('')

const variables = computed(() => ({ query: query.value }))

const { result, load } = useSearchLazyQuery(variables)

const showResults = computed(() => query.value.length > 1)
const results = computed(() => result.value?.search as SearchResult | undefined)

watch(query, () => {
  if (query.value.length > 1) {
    load()
  }
})

const styles = computed(() => ({
  height: `${device.viewportHeight.value}px`,
}))

const handleClearClick = () => {
  query.value = ''
}

const blur = () => {
  input.value?.blur()
}

onMounted(() => {
  input.value?.focus()
  window.addEventListener('scroll', blur)
})

onBeforeUnmount(() => {
  window.removeEventListener('scroll', blur)
})
</script>
